import React, { useState, useEffect } from 'react';
import { Webhook } from '../../../../types';
import { Button } from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Switch from '../../../../components/ui/Switch';
import Checkbox from '../../../../components/ui/Checkbox';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { notify } from '../../../../utils/toast';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter
} from '../../../../components/ui/sheet';

interface WebhookFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: Omit<Webhook, 'id' | 'createdAt'>) => void;
    webhookToEdit?: Webhook;
}

const AVAILABLE_EVENTS = [
    { id: 'lead.created', label: 'Negociação Criada' },
    { id: 'lead.updated', label: 'Negociação Atualizada' },
    { id: 'lead.stage_changed', label: 'Etapa Alterada' },
    { id: 'contact.created', label: 'Contato Criado' },
    { id: 'deal.won', label: 'Negociação Ganha' },
    { id: 'deal.lost', label: 'Negociação Perdida' }
];

const WebhookForm: React.FC<WebhookFormProps> = ({ isOpen, onClose, onSave, webhookToEdit }) => {
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [events, setEvents] = useState<string[]>([]);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (isOpen) {
            if (webhookToEdit) {
                setName(webhookToEdit.name);
                setUrl(webhookToEdit.url);
                setEvents(webhookToEdit.events);
                setIsActive(webhookToEdit.isActive);
            } else {
                setName('');
                setUrl('');
                setEvents([]);
                setIsActive(true);
            }
        }
    }, [isOpen, webhookToEdit]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ name, url, events, isActive });
    };

    const toggleEvent = (eventId: string) => {
        if (events.includes(eventId)) {
            setEvents(events.filter(e => e !== eventId));
        } else {
            setEvents([...events, eventId]);
        }
    };

    const handleTestCall = () => {
        if (!url) {
            notify.error('Informe uma URL de destino para testar');
            return;
        }
        // Simular chamada de teste com sucesso para feedback na UI
        notify.success('Test call successful: 200 OK');
    };

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto flex flex-col p-6">
                <SheetHeader className="px-0 pb-6 border-b border-border text-left">
                    <SheetTitle>
                        {webhookToEdit ? 'Editar Webhook' : 'Novo Webhook'}
                    </SheetTitle>
                    <SheetDescription>
                        {webhookToEdit 
                            ? 'Atualize as configurações e eventos deste webhook.' 
                            : 'Configure um novo webhook para receber eventos do sistema no seu servidor.'}
                    </SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} id="webhook-form" className="flex-1 py-6 flex flex-col gap-6">
                    <Input
                        id="webhook-name"
                        label="Nome"
                        name="name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Integração CRM"
                    />

                    <Input
                        id="webhook-url"
                        label="URL de Destino"
                        name="url"
                        type="url"
                        required
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://sua-api.com/webhook"
                    />

                    <div className="input-wrap">
                        <div className="input-label">Eventos</div>
                        <div className="space-y-3 rounded-lg border border-border bg-surface-subtle p-3 custom-scrollbar">
                            {AVAILABLE_EVENTS.map((event) => (
                                <Checkbox
                                    key={event.id}
                                    id={`event-${event.id}`}
                                    label={event.label}
                                    checked={events.includes(event.id)}
                                    onChange={() => toggleEvent(event.id)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-border bg-surface-subtle p-3">
                        <Switch
                            id="webhook-is-active"
                            checked={isActive}
                            onChange={setIsActive}
                            label="Status do Webhook"
                            description="Determine se este webhook irá disparar ou não"
                        />
                    </div>
                </form>

                <SheetFooter className="px-0 pt-6 mt-auto border-t border-border flex-col sm:flex-row gap-3">
                    <Button type="button" variant="secondary" onClick={handleTestCall} className="w-full sm:w-auto">
                        <AppIcon name="play" className="w-4 h-4 mr-2" />
                        Run Test
                    </Button>
                    <div className="flex-1" />
                    <Button type="button" variant="ghost" onClick={onClose} className="w-full sm:w-auto">
                        Cancelar
                    </Button>
                    <Button type="submit" form="webhook-form" className="w-full sm:w-auto">
                        Salvar Webhook
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default WebhookForm;
